/**
 * Builds a manual Bdx_Key for a Co-Lead UMR entry that has no exact GlobalUMR match.
 * Format: <UMR>_<Risk>_<Cat>_<Section>_<Currency>_<YoA>[_<TPA prefix>-<TPA id>_<Coverholder prefix>-<Coverholder id>]
 * Mirrors Lincoln's own GlobalUMR.create_new_key()/rls_prefix() exactly (confirmed
 * against /Users/shahidzia/lincoln/backend/project/rls_filterset/models.py):
 * the base 6 parts are UMR/Risk_Code/Cat_Code/Section_No/Currency/Year_of_Account in
 * that order; the prefix is the name with spaces stripped, first 3 chars, uppercased
 * (not "strip all non-letters" - digits in a company name are kept); and the TPA and
 * Coverholder extension is only ever appended as a PAIR (using "0" as a placeholder
 * for whichever one isn't known) so the key always has either 6 or 8 parts, matching
 * Lincoln's extract_tpa_and_coverholder_from_key() which requires exactly 8.
 */
export function buildManualBdxKey({
  umr,
  riskCode,
  catCode,
  sectionNo,
  currency,
  yearOfAccount,
  tpaCompany,
  coverholderCompany
}) {
  const rlsPrefix = (name) => (name || '').replace(/\s/g, '').slice(0, 3).toUpperCase()

  const parts = [umr, riskCode, catCode, sectionNo, currency, yearOfAccount].map((part) =>
    part === undefined || part === null ? '' : String(part)
  )

  const hasTpa = tpaCompany && tpaCompany.id
  const hasCoverholder = coverholderCompany && coverholderCompany.id

  if (hasTpa || hasCoverholder) {
    const tpaSegment = hasTpa ? `${rlsPrefix(tpaCompany.name)}-${tpaCompany.id}` : '0'
    const coverholderSegment = hasCoverholder ? `${rlsPrefix(coverholderCompany.name)}-${coverholderCompany.id}` : '0'
    parts.push(tpaSegment, coverholderSegment)
  }

  return parts.join('_')
}

/**
 * Case-insensitive equality check used to reject adding a UMR/key that's
 * already present in the Co-Lead UMRs table.
 */
export function isDuplicateBdxKey(newKey, existingKeys) {
  const normalized = (newKey || '').trim().toLowerCase()
  return (existingKeys || []).some((key) => (key || '').trim().toLowerCase() === normalized)
}

/**
 * Sums the share_percentage across ALL added Co-Lead rows, regardless of the
 * funding_required toggle. The share split is the full allocation of the claim
 * and must total 100% to proceed; toggling funding off means a row doesn't need
 * funding (and is excluded from exports), but it still holds its share of the
 * allocation and so still counts toward the 100% gate. Rounds to 2dp to absorb
 * float drift before the 100% equality check gating "Next".
 */
export function totalSharePercentage(rows) {
  const total = (rows || [])
    .reduce((sum, row) => sum + (Number(row.share_percentage) || 0), 0)
  return Math.round(total * 100) / 100
}

/**
 * Flattens the bulk GlobalUMR+claims cache (one entry per key, each carrying
 * a `claim_details` array) into one row per (key, claim) combination, so both
 * key-level fields (umr/risk_code/cat_code/section_no/currency_type/
 * year_of_account/tpa/underwriter/broker/coverholder) and claim-level fields
 * (claim_ref/insured_name/date_of_loss/...) are filterable together in the
 * Add-UMR faceted search. A key with no claims on file still gets one row
 * (with blank claim fields) so it stays selectable by its key fields alone.
 */
// The KEY-identity fields belong to the GlobalUMR key, not to any individual
// claim. If the Lincoln claim payload ever carries a field with one of these
// names (e.g. a claim-level "cat_code"/"date"-like value), a naive
// { ...keyFields, ...claim } spread lets the claim value OVERWRITE the real key
// value - which surfaced as the CAT Code / Section # dropdown showing claim
// dates instead of codes. These must always come from the key.
const KEY_IDENTITY_FIELDS = [
  'umr', 'umr_rc_cc_sn', 'risk_code', 'cat_code', 'section_no',
  'currency_type', 'year_of_account', 'tpa', 'underwriter', 'broker', 'coverholder'
]

export function flattenUmrClaimsCache(cache) {
  return (Array.isArray(cache) ? cache : []).flatMap((key) => {
    const { claim_details: claimDetails, ...keyFields } = key
    const claims = claimDetails && claimDetails.length ? claimDetails : [{}]
    // One row per DISTINCT claim ref on this key. rig_datarows holds a claim
    // once per reporting period, so a claim reported in many bordereaux arrives
    // as many identical claim_details entries - which surfaced as the same
    // UMR/Claim Ref/Underwriter/Broker listed over and over in the search
    // results. Lincoln now dedupes in SQL, but it can only collapse rows
    // identical in EVERY column it selects: a claim whose insured name, dates
    // or cause of loss were amended between periods still arrives more than
    // once, and those rows are indistinguishable in this table (it shows only
    // UMR / Claim Ref / Underwriter / Broker). The search offers a claim ref to
    // add, not a specific period's snapshot, so keep the first occurrence per
    // claim ref and drop later ones.
    const seenClaimRefs = new Set()
    const deduped = claims.filter((claim) => {
      const ref = String((claim && claim.claim_ref) || '').trim().toLowerCase()
      // A key with no claims on file keeps its single blank-claim row.
      if (!ref) return true
      if (seenClaimRefs.has(ref)) return false
      seenClaimRefs.add(ref)
      return true
    })
    return deduped.map((claim) => {
      // Claim fields fill everything they add, but the key-identity fields are
      // re-asserted from the key afterwards so a same-named claim field can
      // never clobber the key's own UMR/Risk/Cat/Section/Currency/YoA/parties.
      const merged = { ...keyFields, ...claim }
      KEY_IDENTITY_FIELDS.forEach((f) => {
        if (f in keyFields) merged[f] = keyFields[f]
      })
      return merged
    })
  })
}

export const CO_LEAD_FACETED_FIELDS = [
  'umr', 'risk_code', 'cat_code', 'section_no', 'currency_type', 'year_of_account', 'claim_ref'
]

/**
 * Fields the top-of-step "Claim Reference" free-text search actually looks at.
 * The old search tested EVERY field of every row (via Object.values), which on
 * a production-sized flattened cache (tens of thousands of rows x ~15 fields)
 * meant hundreds of thousands of String()/toLowerCase() calls per query. Users
 * only ever search by these identity fields, so restricting to them - and
 * precomputing one lowercased blob per row (see buildSearchIndex) - is what
 * makes the search responsive.
 */
export const CO_LEAD_SEARCH_FIELDS = [
  'umr', 'claim_ref', 'underwriter', 'broker', 'coverholder', 'tpa',
  'umr_rc_cc_sn', 'insured_name'
]

/**
 * Precomputes, once per flattened cache, a parallel array of lowercased
 * search strings - one per row, each the row's searchable fields joined and
 * lowercased a single time. searchResults then only has to do
 * `blob.includes(query)` per row instead of re-scanning and re-lowercasing
 * every field on every keystroke. Returns { rows, blobs } where blobs[i]
 * corresponds to rows[i].
 */
export function buildSearchIndex(flattenedRows, fields = CO_LEAD_SEARCH_FIELDS) {
  const rows = Array.isArray(flattenedRows) ? flattenedRows : []
  const blobs = rows.map((row) => {
    let blob = ''
    for (let f = 0; f < fields.length; f += 1) {
      const value = row[fields[f]]
      if (value !== null && value !== undefined && value !== '') {
        blob += String(value) + ' '
      }
    }
    return blob.toLowerCase()
  })
  return { rows, blobs }
}

function rowMatchesFilters(row, activeFilters, excludeField) {
  return Object.keys(activeFilters).every((field) => {
    if (field === excludeField) return true
    return String(row[field] || '').toLowerCase().includes(String(activeFilters[field]).toLowerCase())
  })
}

/**
 * Standard cross-filtering ("faceted search") over the flattened cache: given
 * whichever fields the user has already typed/selected, returns the rows
 * matching ALL of them, plus - per filterable field - the distinct values
 * still available once every OTHER field's current filter is applied (so
 * picking a value in one field narrows the others' dropdown options without
 * a field's own choice collapsing its own list to just itself). All done
 * client-side against the once-fetched cache - no network call per keystroke.
 */
export function facetedFilter(rows, filters, fields = CO_LEAD_FACETED_FIELDS) {
  const activeFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )

  const matches = (rows || []).filter((row) => rowMatchesFilters(row, activeFilters, null))

  const optionsByField = {}
  fields.forEach((field) => {
    const subset = (rows || []).filter((row) => rowMatchesFilters(row, activeFilters, field))
    optionsByField[field] = [...new Set(
      subset.map((row) => row[field]).filter((value) => value !== undefined && value !== null && value !== '')
    )]
  })

  return { matches, optionsByField }
}

/**
 * Single-field variant of facetedFilter's optionsByField computation - one
 * filter pass over `rows` instead of `fields.length` passes, and skips the
 * `matches` computation entirely. Meant for an autocomplete input that only
 * ever needs ONE field's option list at a time (whichever field currently
 * has focus) rather than every field's at once on every keystroke - calling
 * facetedFilter() with a full field list here scales as O(fields x rows) per
 * keystroke, which gets slow fast once the bulk cache has real production
 * volume.
 */
export function facetedOptionsForField(rows, filters, field) {
  const activeFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )

  const subset = (rows || []).filter((row) => rowMatchesFilters(row, activeFilters, field))

  // "0" is Lincoln's "not specified" placeholder for the numeric-style key
  // segments (risk/cat/section). It is never a real Currency, so drop it from
  // the currency suggestions (otherwise a stray "0" shows in that dropdown).
  const dropZero = field === 'currency_type'

  return [...new Set(
    subset
      .map((row) => row[field])
      .filter((value) => value !== undefined && value !== null && value !== '')
      .filter((value) => !(dropZero && String(value).trim() === '0'))
  )]
}
